// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!

import type { CelulaCompacta } from '@xlv-gva-mpt/dominio';
import { BANDEIRA } from '@xlv-gva-mpt/dominio';

/**
 * ⚠️⚠️ **AS BARRAS DE UMA LINHA DE SUBCONTRATADO — uma barra por SERVIÇO.**
 *
 * 📗 Cliente (B28, `bugs_duvidasv1.pdf` pág. 22-23): *«Seria conveniente também, no mapa
 * subdividir subcontratos por locais e de serviço.»* · e (C₂, §4.3 do desenho da agilidade):
 * *«Os subcontratados têm de dar para ver os dias igualmente como se fosse um autocarro
 * normal; a única diferença é que não se indica o motorista.»*
 *
 * ---------------------------------------------------------------------------
 * ⚠️⚠️ O QUE SAIU DAQUI COM O C₂, E PORQUÊ
 * ---------------------------------------------------------------------------
 *
 * Até ao C₂ este módulo repartia os serviços simultâneos de uma empresa por **bandas** dentro
 * de uma linha, com **tecto 4** e uma calha a contar o que não coubesse (`subdividirSubcontratado`,
 * `bandasDoSubcontratado`, `TECTO_BANDAS_SUB`, `topoDaBanda`, `alturaDaLinhaSub`, …). 📌 A
 * repartição passou a ser em **linhas** e vive em `linhas-do-subcontratado.ts`, que a faz
 * **sobre o ano** — e com uma linha por serviço não há tecto, não há calha e não há geometria
 * de banda. O que resta aqui é o que continua a ser verdade: **onde é que uma barra começa e
 * acaba**.
 *
 * ⚠️ **A altura da linha volta a ser a do CSS.** `.sub-barra { top: 5px; height: 20px }` é
 * outra vez a única fonte, para todas as linhas e não só para 56 % delas — o TSX deixou de
 * escrever geometria vertical nenhuma.
 */

/** Uma barra: **um serviço**, ao longo dos dias em que ele corre. */
export interface BarraDeSubcontratado {
  readonly d0: number;
  readonly d1: number;
  readonly cliente: number;
  readonly itinerario: number;
  readonly corrida: number;
  // ⚠️⚠️ **AQUI ESTAVAM `localidade0` e `localidade1`, e saíram em 2026-09-16.** Elas eram o
  //    paliativo do B28: a barra trazia o PRIMEIRO e o ÚLTIMO local (`BCN→MAD`) porque *«a
  //    secção não tem banda de localidade por dia»*. 📗 O cliente pediu a banda a sério duas
  //    vezes e ela existe agora (`BandaDeLocais` em `Mapa.tsx`), que lê a localidade **do dia**
  //    directamente da célula. 📌 Os dois campos ficaram sem **nenhum** leitor de produção — só
  //    um critério os lia — e um campo que só o teste lê é um campo que finge estar em uso.
  /** Qualquer dia da barra em estado de reserva — a mesma regra da frota. */
  readonly reserva: boolean;
}

/**
 * A identidade de um serviço, para o reconhecer de um dia para o outro.
 *
 * ⚠️ **A chave é a CORRIDA e não o cliente** — é a lição F-19: 2 679 assinaturas de rota
 * servem 15 132 corridas, e é a corrida que liga a mesma viagem ao longo dos dias. Sem
 * corrida cai-se no par cliente+itinerário, que é o melhor que existe.
 */
export function identidadeDoServico(c: CelulaCompacta): string {
  return c[10] >= 0 ? `c:${c[10]}` : `k:${c[0]}|${c[5]}`;
}

/**
 * A ordem **estável** dos serviços de um dia.
 *
 * ⚠️⚠️ **NÃO SE PODE CONFIAR NA ORDEM DO VECTOR, e isso não é hipótese.** A consulta do mapa
 * ordena por `a.viatura_id, a.data` (`dados-mapa.ts`) — e uma linha subcontratada tem
 * `viatura_id` a `NULL`, logo **a ordem entre dois serviços da mesma empresa no mesmo dia não
 * está especificada**. Se a linha saísse da posição no vector, o mesmo serviço podia mudar de
 * linha entre dois carregamentos — e a linha é o que a selecção, o realce e os botões apontam.
 */
export function ordenarServicosDoDia(
  servicos: readonly CelulaCompacta[],
): readonly CelulaCompacta[] {
  return [...servicos].sort((a, b) => a[10] - b[10] || a[0] - b[0] || a[5] - b[5] || a[1] - b[1]);
}

/**
 * As barras de **uma linha** dentro da janela `[dia0, dia1)`.
 *
 * ⚠️ Recebe a grelha de uma linha (`LinhaDeSubcontratado.dias`), que por construção tem **zero
 * ou um** serviço por dia — e é por isso que aqui não há bandas, nem tecto, nem escondidos. Um
 * dia sem serviço fecha a barra; um serviço com um buraco no meio são duas ocorrências, que é
 * o que o plano diz.
 */
export function barrasDaLinhaSub(
  dias: readonly (readonly CelulaCompacta[])[],
  dia0: number,
  dia1: number,
): readonly BarraDeSubcontratado[] {
  const barras: BarraDeSubcontratado[] = [];
  let aberta: { readonly chave: string; readonly indice: number } | null = null;

  for (let d = dia0; d < dia1; d++) {
    const c = (dias[d] ?? [])[0];
    if (c === undefined) {
      aberta = null;
      continue;
    }
    const chave = identidadeDoServico(c);
    if (aberta !== null && aberta.chave === chave) {
      const barra = barras[aberta.indice];
      if (barra !== undefined) {
        barras[aberta.indice] = {
          ...barra,
          d1: d,
          // ⚠️⚠️ **QUALQUER dia da barra**, e não o primeiro: uma reserva que cobrisse só parte
          //    do intervalo ficava invisível se o primeiro dia fosse firme. É a mesma regra que
          //    a frota aplica ao acumular as bandeiras da corrida.
          reserva: barra.reserva || (c[4] & BANDEIRA.RESERVA) !== 0,
        };
      }
      continue;
    }
    barras.push({
      d0: d,
      d1: d,
      cliente: c[0] ?? -1,
      itinerario: c[5] ?? -1,
      corrida: c[10] ?? -1,
      reserva: (c[4] & BANDEIRA.RESERVA) !== 0,
    });
    aberta = { chave, indice: barras.length - 1 };
  }

  return barras;
}

// ===========================================================================
// O EXTENTO DE UM SERVIÇO — uma só formulação, com três leitores
// ===========================================================================

/**
 * De onde a onde vai o serviço que está em `linha[dia][indiceNoDia]`.
 *
 * ⚠️⚠️ **EXISTE PARA MATAR UMA DIVERGÊNCIA.** A regra de continuidade da barra vivia em
 * **três** sítios — a barra em `Mapa.tsx`, o cartão do cursor (`CartaoSub`) e o painel
 * (`detalheSub`) — e as três traziam a mesma condição extra: *«e o mesmo NÚMERO de serviços
 * simultâneos»*. Essa condição existia porque a barra levava uma **contagem por dia** («2×»),
 * e morreu com ela.
 *
 * ⚠️ **`indiceNoDia` deixou de ter chamador com valor diferente de `0`, e isso é o C₂:** quem
 * pergunta pergunta sobre **uma linha**, onde há no máximo um serviço por dia. O parâmetro
 * fica porque a função também serve a grelha da EMPRESA (`viagensDoSubcontratado`), onde os
 * serviços do dia são vários e o chamador diz qual.
 *
 * ⚠️ A identidade é a CORRIDA (F-19) — é a mesma `identidadeDoServico` que constrói as barras,
 * e não uma segunda leitura dela.
 */
export function extentoDoServico(
  linha: readonly (readonly CelulaCompacta[])[],
  dia: number,
  indiceNoDia = 0,
): { readonly d0: number; readonly d1: number } {
  const c = (linha[dia] ?? [])[indiceNoDia];
  if (c === undefined) return { d0: dia, d1: dia };
  const chave = identidadeDoServico(c);
  const tem = (d: number): boolean =>
    (linha[d] ?? []).some((x) => identidadeDoServico(x) === chave);
  let d0 = dia;
  while (d0 > 0 && tem(d0 - 1)) d0--;
  let d1 = dia;
  while (d1 + 1 < linha.length && tem(d1 + 1)) d1++;
  return { d0, d1 };
}
